import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { UserNotification, NotificationPriority } from '../../types/user';
import { formatDistanceToNow } from 'date-fns';

interface NotificationItemProps {
  notification: UserNotification;
  onMarkAsRead: (id: string) => void;
  onMarkAsUnread: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onDelete: (id: string) => void;
  onNavigate?: (url: string) => void;
}

/**
 * NotificationItem Component
 * Displays a single notification with actions (mark read, archive, delete)
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onMarkAsUnread,
  onArchive,
  onUnarchive,
  onDelete,
  onNavigate,
}) => {
  // Get icon based on priority
  const getPriorityIcon = () => {
    switch (notification.priority) {
      case NotificationPriority.CRITICAL:
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case NotificationPriority.HIGH:
        return <WarningIcon sx={{ color: 'warning.main' }} />;
      case NotificationPriority.NORMAL:
        return <InfoIcon sx={{ color: 'info.main' }} />;
      case NotificationPriority.LOW:
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      default:
        return <InfoIcon sx={{ color: 'info.main' }} />;
    }
  };

  // Get priority color
  const getPriorityColor = () => {
    switch (notification.priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'success';
      default:
        return 'default';
    }
  };

  const handleActionClick = () => {
    if (notification.actionUrl && onNavigate) {
      onNavigate(notification.actionUrl);
      if (!notification.read) {
        onMarkAsRead(notification.id);
      }
    }
  };

  return (
    <Card
      sx={{
        mb: 1,
        backgroundColor: notification.read ? 'transparent' : 'action.hover',
        borderLeft: notification.read ? 'none' : '4px solid',
        borderLeftColor: notification.read ? 'transparent' : `${getPriorityColor()}.main`,
        opacity: notification.archived ? 0.6 : 1,
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Priority Icon */}
          <Box sx={{ pt: 0.5 }}>{getPriorityIcon()}</Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title and Priority Badge */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: notification.read ? 400 : 700,
                  flex: 1,
                }}
              >
                {notification.title}
              </Typography>
              {notification.priority && notification.priority !== NotificationPriority.NORMAL && (
                <Chip
                  label={notification.priority.toUpperCase()}
                  size="small"
                  color={getPriorityColor() as any}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>

            {/* Message */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {notification.message}
            </Typography>

            {/* Metadata */}
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
              </Typography>
              {notification.archived && (
                <Chip label="Archived" size="small" variant="outlined" sx={{ height: 20 }} />
              )}
            </Stack>

            {/* Action Button */}
            {notification.actionUrl && (
              <Button
                size="small"
                variant="outlined"
                onClick={handleActionClick}
                sx={{ mt: 1 }}
              >
                {notification.actionLabel || 'View'}
              </Button>
            )}
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={0.5}>
            {/* Mark as Read/Unread */}
            {!notification.archived && (
              <IconButton
                size="small"
                onClick={() =>
                  notification.read
                    ? onMarkAsUnread(notification.id)
                    : onMarkAsRead(notification.id)
                }
                title={notification.read ? 'Mark as unread' : 'Mark as read'}
              >
                <CheckCircleIcon
                  fontSize="small"
                  sx={{
                    color: notification.read ? 'success.main' : 'action.disabled',
                  }}
                />
              </IconButton>
            )}

            {/* Archive/Unarchive */}
            <IconButton
              size="small"
              onClick={() =>
                notification.archived
                  ? onUnarchive(notification.id)
                  : onArchive(notification.id)
              }
              title={notification.archived ? 'Unarchive' : 'Archive'}
            >
              {notification.archived ? (
                <UnarchiveIcon fontSize="small" />
              ) : (
                <ArchiveIcon fontSize="small" />
              )}
            </IconButton>

            {/* Delete */}
            {notification.dismissible !== false && (
              <IconButton
                size="small"
                onClick={() => onDelete(notification.id)}
                title="Delete"
                color="error"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
